import { call, takeLatest } from 'redux-saga/effects';
import {
  activateClinicalTrial,
  createClinicalTrial,
  fetchClinicalTrialById,
  fetchClinicalTrialByIdReset,
  updateClinicalTrial,
} from '@api/clinicalTrials';
import { createSite, deleteSite, updateSite } from '@api/sites';
import { actions } from './clinicalTrial.slice';
import { actions as activateActions } from '../activate/activateClinicalTrialSlice';
import {
  ClinicalTrialCreateAction,
  ClinicalTrialDeleteSiteAction,
  ClinicalTrialFetchAction,
  CreateClinicalTrialSiteAction,
  CreateCohortAction,
  CreateVisitAction,
  DeleteCohortAction,
  DeleteVisitAction,
  EditClinicalTrialSiteAction,
  UpdateClinicalTrialAction,
  UpdateCohortAction,
  UpdateVisitAction,
} from './clinicalTrial.types';
import { AxiosError } from 'axios';
import { Resp, dispatch } from '../helpers';
import { runToast, showErrorToast } from '../tools';
import { goToRoute } from '@dynamika-shared/utils/sagaRouter';
import { prepareUrl } from 'src/utils/routerHelpers';
import { routesPath } from '@router/routes';
import { ClinicalTrialActivateActions } from '@restore/activate/activate.types';
import { createCohort, deleteCohort, updateCohort } from '@api/cohorts';
import { createVisit, deleteVisit, updateVisit } from '@api/visits';
import { ACTIVATION_ERROR_MESSAGES } from '../../constants/activationMessages';

export function* fetchClinicalTrialWorker(action: ClinicalTrialFetchAction) {
  try {
    const response: Resp<typeof fetchClinicalTrialById> = yield call(
      fetchClinicalTrialById,
      action.payload
    );
    yield dispatch(actions.fetchClinicalTrialSuccess(response?.data));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield call(runToast, 'error', error.message);
      yield dispatch(
        actions.fetchClinicalTrialFailure({ message: error.message } as Error)
      );
    } else {
      yield dispatch(actions.fetchClinicalTrialFailure(error as Error));
      yield call(runToast, 'error', 'Fetch clinical trial error');
    }
  }
}

export function* deleteSiteWorker({
  payload: { clinicalTrialId, siteId },
}: ClinicalTrialDeleteSiteAction) {
  try {
    yield call(deleteSite, clinicalTrialId, siteId);
    yield call(runToast, 'success', 'Site successfully deleted');
    yield dispatch(actions.deleteSiteSuccess(null));
    yield dispatch(actions.fetchClinicalTrial(clinicalTrialId));
  } catch (error) {
    yield showErrorToast(error, 'Delete site error');
  }
}

export function* createClinicalTrialWorker({
  payload,
}: ClinicalTrialCreateAction) {
  try {
    const response: Resp<typeof createClinicalTrial> = yield call(
      createClinicalTrial,
      payload
    );
    yield dispatch(actions.createClinicalTrialSuccess(null));
    yield call(runToast, 'success', 'Clinical Trial created successfully');
    yield goToRoute(
      prepareUrl(routesPath.clinicalTrialGeneralInformation, [response.data.id])
    );
  } catch (error) {
    yield dispatch(actions.createClinicalTrialFailure(null));
    yield showErrorToast(error, 'Clinical trial create error');
  }
}

export function* updateClinicalTrialWorker({
  payload,
}: UpdateClinicalTrialAction) {
  try {
    yield call(updateClinicalTrial, payload.clinicalTrialId, payload.data);
    yield dispatch(actions.updateClinicalTrialSuccess(null));
    yield call(runToast, 'success', 'Clinical trial updated successfully');
    yield call(fetchClinicalTrialByIdReset);
    yield dispatch(actions.fetchClinicalTrial(payload.clinicalTrialId));
  } catch (error) {
    yield dispatch(actions.updateClinicalTrialFailure(null));
    yield showErrorToast(error, 'Clinical trial update error');
  }
}

export function* activateClinicalTrialWorker({
  payload,
}: ClinicalTrialActivateActions) {
  try {
    yield call(activateClinicalTrial, payload.id);
    yield dispatch(activateActions.activateClinicalTrialSuccess(null));
    yield dispatch(activateActions.clearErrors());
    yield dispatch(actions.fetchClinicalTrial(payload.id));
    yield call(runToast, 'success', 'Activation successfully');
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      for (const err of error.response.data.errors) {
        let name = err.name === 'cohorts' ? 'visits' : err.name;
        if(err.name === 'dataTypeRelations') {
          name = 'forms';
        }
        const errors = err.requiredFields.length
          ? err.requiredFields
          : ['required'];
        yield dispatch(activateActions.setErrors({ name, errors }));

        if (payload.currentRoute === name) {
          const message =
            ACTIVATION_ERROR_MESSAGES[
              payload.currentRoute as keyof typeof ACTIVATION_ERROR_MESSAGES
            ] || ACTIVATION_ERROR_MESSAGES.defaultMessage;
          yield call(runToast, 'error', message, 'Attention');
        }
      }
    } else {
      yield call(runToast, 'error', 'Clinical Trial activation failed');
    }
    yield dispatch(activateActions.activateClinicalTrialFailure(null));
  }
}

export function* createSiteWorker({ payload }: CreateClinicalTrialSiteAction) {
  try {
    yield call(createSite, payload.clinicalTrialId, payload.values);
    yield dispatch(actions.createSiteSuccess(null));
    yield dispatch(actions.fetchClinicalTrial(payload.clinicalTrialId));
    yield call(runToast, 'success', 'Site created successfully');
  } catch (error) {
    yield dispatch(actions.createSiteFailure(null));
    yield showErrorToast(error, 'Site creation failed');
  }
}

export function* updateSiteWorker({ payload }: EditClinicalTrialSiteAction) {
  try {
    yield call(
      updateSite,
      payload.clinicalTrialId,
      payload.siteId,
      payload.values
    );
    yield dispatch(actions.updateSiteSuccess(null));
    yield dispatch(actions.fetchClinicalTrial(payload.clinicalTrialId));
    yield call(runToast, 'success', 'Site updated successfully');
  } catch (error) {
    yield dispatch(actions.updateSiteFailure(null));
    yield showErrorToast(error, 'Site update failed');
  }
}

export function* createCohortWorker({ payload }: CreateCohortAction) {
  try {
    yield call(createCohort, payload.clinicalTrialId, payload.data);
    yield dispatch(actions.createCohortSuccess(null));
    yield dispatch(actions.fetchClinicalTrial(payload.clinicalTrialId));
    yield call(runToast, 'success', 'Cohort created successfully');
    yield dispatch(activateActions.setErrors({ name: 'visits', errors: [] }));
  } catch (error) {
    yield dispatch(actions.createCohortFailure(null));
    yield showErrorToast(error, 'Failed to create cohort.');
  }
}
export function* updateCohortWorker({ payload }: UpdateCohortAction) {
  try {
    yield call(
      updateCohort,
      payload.clinicalTrialId,
      payload.cohortId,
      payload.data
    );
    yield dispatch(actions.updateCohortSuccess(null));
    yield dispatch(actions.fetchClinicalTrial(payload.clinicalTrialId));
    yield call(runToast, 'success', 'Cohort updated successfully');
  } catch (error) {
    yield dispatch(actions.updateCohortFailure(null));
    yield showErrorToast(error, 'Failed to upload cohort.');
  }
}

export function* deleteCohortWorker({ payload }: DeleteCohortAction) {
  try {
    yield call(deleteCohort, payload.clinicalTrialId, payload.cohortId);
    yield dispatch(actions.deleteCohortSuccess(null));
    const response: Resp<typeof fetchClinicalTrialById> = yield call(
      fetchClinicalTrialById,
      payload.clinicalTrialId
    );
    yield dispatch(actions.fetchClinicalTrialSuccess(response?.data));
    yield call(runToast, 'success', 'Cohort deleted successfully');
    yield call(payload.onSuccess);
  } catch (error) {
    yield dispatch(actions.deleteCohortFailure(null));
    yield showErrorToast(error, 'Failed to delete cohort.');
  }
}

export function* createVisitWorker({ payload }: CreateVisitAction) {
  try {
    yield call(createVisit, payload.clinicalTrialId, payload.data);
    const response: Resp<typeof fetchClinicalTrialById> = yield call(
      fetchClinicalTrialById,
      payload.clinicalTrialId
    );
    yield dispatch(actions.fetchClinicalTrialSuccess(response?.data));
    yield dispatch(actions.createVisitSuccess(null));
    yield call(runToast, 'success', 'Visit created successfully');
    yield dispatch(activateActions.setErrors({ name: 'visits', errors: [] }));
    yield call(payload.onSuccess);
  } catch (error) {
    yield dispatch(actions.createVisitFailure(null));
    yield showErrorToast(error, 'Failed to create visit.');
  }
}

export function* updateVisitWorker({ payload }: UpdateVisitAction) {
  try {
    yield call(
      updateVisit,
      payload.clinicalTrialId,
      payload.visitId,
      payload.data
    );
    const response: Resp<typeof fetchClinicalTrialById> = yield call(
      fetchClinicalTrialById,
      payload.clinicalTrialId
    );
    yield dispatch(actions.fetchClinicalTrialSuccess(response?.data));
    yield dispatch(actions.updateVisitSuccess(null));

    yield call(runToast, 'success', 'Visit updated successfully');
    yield dispatch(activateActions.setErrors({ name: 'visits', errors: [] }));
    yield call(payload.onSuccess);
  } catch (error) {
    yield dispatch(actions.updateVisitFailure(null));
    yield showErrorToast(error, 'Failed to update visit.');
  }
}

export function* deleteVisitWorker({ payload }: DeleteVisitAction) {
  try {
    yield call(deleteVisit, payload.clinicalTrialId, payload.visitId);

    yield dispatch(actions.deleteVisitSuccess(null));
    const response: Resp<typeof fetchClinicalTrialById> = yield call(
      fetchClinicalTrialById,
      payload.clinicalTrialId
    );
    yield dispatch(actions.fetchClinicalTrialSuccess(response?.data));
    yield call(runToast, 'success', 'Visit deleted successfully');
    yield call(payload.onSuccess);
  } catch (error) {
    yield dispatch(actions.deleteVisitFailure(null));
    yield showErrorToast(error, 'Failed to delete visit.');
  }
}

export function* clinicalTrialSaga() {
  yield takeLatest(actions.fetchClinicalTrial.type, fetchClinicalTrialWorker);
  yield takeLatest(actions.createClinicalTrial.type, createClinicalTrialWorker);
  yield takeLatest(
    actions.updateClinicalTrialRequest.type,
    updateClinicalTrialWorker
  );
  yield takeLatest(
    activateActions.activateClinicalTrial.type,
    activateClinicalTrialWorker
  );
  yield takeLatest(actions.createSiteRequest.type, createSiteWorker);
  yield takeLatest(actions.updateSiteRequest.type, updateSiteWorker);
  yield takeLatest(actions.deleteSite.type, deleteSiteWorker);
  yield takeLatest(actions.createCohortRequest.type, createCohortWorker);
  yield takeLatest(actions.updateCohortRequest.type, updateCohortWorker);
  yield takeLatest(actions.deleteCohortRequest.type, deleteCohortWorker);
  yield takeLatest(actions.createVisitRequest.type, createVisitWorker);
  yield takeLatest(actions.updateVisitRequest.type, updateVisitWorker);
  yield takeLatest(actions.deleteVisitRequest.type, deleteVisitWorker);
}
